import React, { useEffect, useState, useMemo } from 'react';
import { 
    List, 
    Avatar, 
    Input, 
    Checkbox, 
    Spin, 
    Empty,
    Typography,
    Tag,
    Button
} from 'antd';
import { TeamOutlined, SearchOutlined, LockOutlined } from '@ant-design/icons';
import moment from 'moment';
import useContactProfileStore from '../../stores/useContactProfileStore';

const { Text } = Typography;

const styles = {
    container: {
        padding: '0 0',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
    },
    searchInput: {
        marginBottom: 12,
        width: 'calc(100% - 16px)',
        marginLeft: 0,
        flexShrink: 0,
    },
    listItem: {
        padding: '8px 12px',
        borderRadius: 6,
        marginBottom: 4,
        transition: 'all 0.2s ease',
        cursor: 'pointer',
    },
    memberItem: {
        backgroundColor: '#f6ffed',
        border: '1px solid #b7eb8f',
    },
    nonMemberItem: {
        backgroundColor: '#fafafa',
        border: '1px solid #f0f0f0',
    },
    groupInfo: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minWidth: 0,
    },
    groupName: {
        fontWeight: 500,
        fontSize: 13,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    },
    groupMeta: {
        fontSize: 11,
        color: '#8c8c8c',
    },
    checkbox: {
        marginRight: 10,
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 40,
        height: '100%',
    },
    header: {
        marginBottom: 12,
        fontSize: 15,
        fontWeight: 600,
        color: '#263238',
        flexShrink: 0,
    },
    privateGroupsNote: {
        marginTop: 12,
        marginRight: 16,
        padding: '8px 12px',
        backgroundColor: '#fffbe6',
        border: '1px solid #ffe58f',
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        color: '#ad6800',
        fontSize: 12,
        flexShrink: 0,
    },
    buttonContainer: {
        textAlign: 'right',
        marginTop: 8,
        paddingRight: 16,
        flexShrink: 0,
    },
    listContainer: {
        flex: 1,
        overflowY: 'auto',
        paddingRight: 16,
        minHeight: 0,
    }
};

const GroupsTab = ({ contact, companyId }) => {
    const [searchText, setSearchText] = useState('');
    const [localMemberships, setLocalMemberships] = useState({});
    const [saving, setSaving] = useState(false);
    
    const {
        companyGroups,
        otherPrivateGroupCount,
        loadingGroups,
        fetchCompanyGroups,
        addToGroup,
        removeFromGroup,
        syncGroups
    } = useContactProfileStore();

    useEffect(() => {
        if (companyId && contact && contact.id) {
            fetchCompanyGroups(companyId, contact.id);
        }
    }, [companyId, (contact && contact.id)]);

    // Reset local state when companyGroups changes
    useEffect(() => {
        setLocalMemberships({});
    }, [companyGroups]);

    // Filter and process groups
    const editableGroups = useMemo(() => {
        const groups = Array.isArray(companyGroups) ? companyGroups : [];
        // Only show groups user can edit (public groups + user's own private groups)
        return groups.filter(group => group.canEdit !== false);
    }, [companyGroups]);

    // Sort groups: members first, then alphabetically
    const sortedGroups = useMemo(() => {
        return [...editableGroups].sort((a, b) => {
            // Members first
            if (a.isMember && !b.isMember) return -1;
            if (!a.isMember && b.isMember) return 1;
            // Then alphabetically
            return (a.name || '').localeCompare(b.name || '');
        });
    }, [editableGroups]);

    const filteredGroups = useMemo(() => {
        if (!searchText.trim()) {
            return sortedGroups;
        }
        const search = searchText.toLowerCase();
        return sortedGroups.filter(group => 
            group.name && group.name.toLowerCase().indexOf(search) !== -1
        );
    }, [sortedGroups, searchText]);

    // Check if there are pending changes
    const hasChanges = Object.keys(localMemberships).length > 0;

    // Get effective membership state (local override or original)
    const getIsMember = (group) => {
        if (localMemberships.hasOwnProperty(group.id)) {
            return localMemberships[group.id];
        }
        return group.isMember;
    };

    const handleToggleGroup = (group) => {
        const currentMembership = getIsMember(group);
        const originalMembership = group.isMember;
        const newMembership = !currentMembership;

        // If toggling back to original state, remove from local changes
        if (newMembership === originalMembership) {
            const updated = { ...localMemberships };
            delete updated[group.id];
            setLocalMemberships(updated);
        } else {
            setLocalMemberships({
                ...localMemberships,
                [group.id]: newMembership
            });
        }
    };

    const handleReset = () => {
        setLocalMemberships({});
    };

    const handleUpdate = async () => {
        setSaving(true);
        try {
            const toAdd = [];
            const toRemove = [];

            Object.keys(localMemberships).forEach(groupId => {
                if (localMemberships[groupId]) {
                    toAdd.push(groupId);
                } else {
                    toRemove.push(groupId);
                }
            });

            // Batch update
            if (toAdd.length > 0 || toRemove.length > 0) {
                await syncGroups(toAdd, toRemove);
            }

            setLocalMemberships({});
        } finally {
            setSaving(false);
        }
    };

    const safeCompanyGroups = Array.isArray(companyGroups) ? companyGroups : [];
    const memberCount = safeCompanyGroups.filter(g => g.isMember && g.canEdit !== false).length;
    const totalMemberCount = memberCount + (otherPrivateGroupCount || 0);

    // Update button style matching Details tab
    const updateButtonStyle = (disabled) => ({
        backgroundColor: disabled ? '#d9d9d9' : '#4cb050',
        borderColor: disabled ? '#d9d9d9' : '#4cb050',
        color: disabled ? 'rgba(0, 0, 0, 0.25)' : '#fff',
    });

    if (loadingGroups) {
        return (
            <div style={styles.loadingContainer}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <span>
                        Member of {totalMemberCount} {totalMemberCount === 1 ? 'Group' : 'Groups'}
                    </span>
                  
                    {otherPrivateGroupCount > 0 && (
                        <div style={styles.privateGroupsNote}>
                            <span>{memberCount} visible </span>
                            <span>
                             + {otherPrivateGroupCount} private {otherPrivateGroupCount === 1 ? 'group' : 'groups'}
                            </span>
                            <LockOutlined />
                        </div>
                    )}
               </div>      
            </div>
            
            <Input
                placeholder="Search groups..."
                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={styles.searchInput}
                allowClear
            />

            {filteredGroups.length === 0 ? (
                <Empty 
                    description={searchText ? "No groups match your search" : "No groups available"} 
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            ) : (
                <div style={styles.listContainer} className="scrollableListDiv">
                    <List
                        dataSource={filteredGroups}
                        renderItem={(group) => {
                            const isMember = getIsMember(group);
                            const isPrivate = group.privacy === 'private';
                            
                            return (
                                <div 
                                    style={{
                                        ...styles.listItem,
                                        ...(isMember ? styles.memberItem : styles.nonMemberItem)
                                    }}
                                    onClick={() => handleToggleGroup(group)}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <Checkbox 
                                            checked={isMember} 
                                            style={styles.checkbox}
                                            onChange={() => handleToggleGroup(group)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                        <Avatar 
                                            icon={<TeamOutlined />} 
                                            size="small"
                                            style={{ 
                                                backgroundColor: isMember ? '#52c41a' : '#1890ff',
                                                marginRight: 10 
                                            }} 
                                        />
                                        <div style={styles.groupInfo}>
                                            <span style={styles.groupName}>
                                                {group.name}
                                                {isPrivate && (
                                                    <Tag 
                                                        icon={<LockOutlined />} 
                                                        color="warning" 
                                                        style={{ fontSize: 10, padding: '0 4px', lineHeight: '16px' }}
                                                    >
                                                        Your Private Group
                                                    </Tag>
                                                )}
                                            </span>
                                            <Text type="secondary" style={styles.groupMeta}>
                                                {group.contact_count || 0} {parseInt(group.contact_count || 0) === 1 ? 'contact' : 'contacts'}
                                                {group.created_at && ` · Created ${moment(group.created_at).format('MMM D, YYYY')}`}
                                            </Text>
                                        </div>
                                    </div>
                                </div>
                            );
                        }}
                    />
                </div>
            )}


            <div style={styles.buttonContainer}>
                {hasChanges && !saving && (
                    <Button onClick={handleReset} style={{ marginRight: 8 }} size="small">
                        Reset
                    </Button>
                )}
                <Button 
                    onClick={handleUpdate}
                    loading={saving} 
                    size="small" 
                    disabled={!hasChanges || saving} 
                    style={updateButtonStyle(!hasChanges || saving)}
                >
                    Update
                </Button>
            </div>
        </div>
    );
};

export default GroupsTab;



